/**
 * A gapless loop as a scheduled chain of overlapping one-shot sources, replacing `loop: true` on a
 * `BufferSourceNode`. Each iteration ends while the next is already ramping in, so the AAC priming
 * silence at the start of every iteration AND the padding silence at the end are masked inside the
 * crossfade window — perceived as one continuous loop.
 *
 * Signal path:
 *
 *   source A → perSourceGain A ──┐
 *   source B → perSourceGain B ──┼─→ sharedGain → destination
 *   source C → perSourceGain C ──┘
 *
 * The per-source gains handle the iteration crossfade; the shared gain is returned for outer
 * control (`LoopPlayer.crossfadeTo` ramps this same node when swapping between loops).
 *
 * `overlapSeconds` defaults to 0.1 — large enough to mask 46 ms of AAC priming + decode jitter,
 * small enough that musical content blends imperceptibly. Capped at half the buffer so very short
 * loops still work.
 */
export function startSeamlessLoop(ctx: AudioContext, params: { buffer: AudioBuffer; destination: AudioNode; overlapSeconds?: number }): { stop: () => void; gainNode: GainNode } {
  const buffer = params.buffer;
  const requestedOverlap = typeof params.overlapSeconds === "number" && Number.isFinite(params.overlapSeconds) ? Math.max(0, params.overlapSeconds) : 0.1;
  const overlap = Math.min(requestedOverlap, buffer.duration / 2);

  const sharedGain = ctx.createGain();
  sharedGain.connect(params.destination);

  let stopped = false;
  let pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let active: { src: AudioBufferSourceNode; gain: GainNode }[] = [];

  const scheduleIteration = (startTime: number): void => {
    if (stopped) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = false;
    const perSourceGain = ctx.createGain();
    if (overlap > 0) {
      // Crossfade in over the leading window, out over the trailing one (the next iteration's lead-in).
      perSourceGain.gain.setValueAtTime(0, startTime);
      perSourceGain.gain.linearRampToValueAtTime(1, startTime + overlap);
      const fadeOutStart = startTime + buffer.duration - overlap;
      perSourceGain.gain.setValueAtTime(1, fadeOutStart);
      perSourceGain.gain.linearRampToValueAtTime(0, fadeOutStart + overlap);
    } else {
      perSourceGain.gain.setValueAtTime(1, startTime);
    }
    src.connect(perSourceGain);
    perSourceGain.connect(sharedGain);
    src.start(startTime);

    const entry = { src, gain: perSourceGain };
    active.push(entry);
    src.onended = () => {
      try {
        src.disconnect();
      } catch {}
      try {
        perSourceGain.disconnect();
      } catch {}
      active = active.filter((e) => e !== entry);
    };

    // The next iteration begins when this one starts its fade-out. The JS call fires ~100 ms
    // ahead so event-loop jitter cannot miss it; `src.start(t)` itself is sample-accurate.
    const nextStartTime = startTime + buffer.duration - overlap;
    const leadSeconds = 0.1;
    const delaySeconds = Math.max(0, nextStartTime - ctx.currentTime - leadSeconds);
    pendingTimeoutId = setTimeout(() => {
      pendingTimeoutId = null;
      scheduleIteration(nextStartTime);
    }, delaySeconds * 1000);
  };

  scheduleIteration(ctx.currentTime);

  const stop = (): void => {
    stopped = true;
    if (pendingTimeoutId !== null) {
      clearTimeout(pendingTimeoutId);
      pendingTimeoutId = null;
    }
    for (const entry of active) {
      try {
        entry.src.stop();
      } catch {}
      try {
        entry.src.disconnect();
      } catch {}
      try {
        entry.gain.disconnect();
      } catch {}
    }
    active = [];
    try {
      sharedGain.disconnect();
    } catch {}
  };

  return { stop, gainNode: sharedGain };
}
